import { useState, useEffect, useCallback } from 'react';
import type { PricingSetting, PricingSettingInput } from '../types';
import {
  fetchPricingSettings,
  updatePricingSettings,
  fetchConfigYaml,
  type ConfigYamlResponse,
} from '../hooks/useMobileSettings';
import { createLogger } from '../../utils/logger';

const log = createLogger('MobileSettingsPage');

function findCommentStart(line: string): number {
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"' && !inSingle) inDouble = !inDouble;
    else if (c === "'" && !inDouble) inSingle = !inSingle;
    else if (c === '#' && !inSingle && !inDouble) {
      if (i === 0 || /\s/.test(line[i - 1])) return i;
    }
  }
  return -1;
}

function renderYamlWithComments(yaml: string) {
  const lines = yaml.split('\n');
  return lines.map((line, idx) => {
    const commentStart = findCommentStart(line);
    const isLast = idx === lines.length - 1;
    if (commentStart === -1) {
      return (
        <span key={idx}>
          {line}
          {!isLast && '\n'}
        </span>
      );
    }
    return (
      <span key={idx}>
        {line.slice(0, commentStart)}
        <span style={{ color: '#6B8295' }}>{line.slice(commentStart)}</span>
        {!isLast && '\n'}
      </span>
    );
  });
}

// Known FAL endpoints used in the application. These are the exact ids sent to
// the provider — the server-side pricing lookup (shared/pricing_settings.py)
// keys on the full endpoint string, so anything missing here can never be
// priced and its generation_log.csv `cost_usd` column stays empty.
const KNOWN_FAL_ENDPOINTS = [
  'fal-ai/moondream3',
  'fal-ai/nano-banana-2/edit',
  'openai/gpt-image-2/edit',
  'fal-ai/gpt-image-1.5/edit',
  'meta/muse-image/edit',
  // Video pipeline — generation models (src/video/fal_models.py)
  'fal-ai/kling-video/v3/pro/image-to-video',
  'fal-ai/kling-video/v3/4k/image-to-video',
  'fal-ai/kling-video/o3/pro/image-to-video',
  'fal-ai/kling-video/o3/4k/image-to-video',
  'minimax/h3-max/image-to-video',
  'fal-ai/veo3.1/fast/first-last-frame-to-video',
  'bytedance/seedance-2.0/image-to-video',
  // Video pipeline — supporting calls
  'fal-ai/bria/background/remove',
  'fal-ai/sam-3/image',
  'Uveye/xfeat-feature-matcher',
  'Uveye/roma-feature-matcher',
  'openrouter/router/vision',
];

// What each endpoint is really billed by, and whether we hold its published
// rate. Anything priced automatically needs no figure here at all; where the
// billing unit isn't a call and we have no published rate, a per-call figure
// entered here is an operator's own average.
const FAL_PRICING_NOTES: Record<string, string> = {
  'fal-ai/kling-video/v3/pro/image-to-video': 'Billed per second of generated video. Priced automatically from the published rate and each request\u2019s duration — leave blank unless you need to override it.',
  'fal-ai/kling-video/v3/4k/image-to-video': 'Billed per second of generated video. Priced automatically from the published rate and each request\u2019s duration — leave blank unless you need to override it.',
  'fal-ai/kling-video/o3/pro/image-to-video': 'Billed per second at the same rates as Kling v3 Pro: $0.112 without audio or $0.168 with audio. Priced automatically from each request\u2019s duration — leave blank unless you need to override it.',
  'fal-ai/kling-video/o3/4k/image-to-video': 'Billed at $0.42 per second of generated video regardless of audio. Priced automatically from each request\u2019s duration — leave blank unless you need to override it.',
  'minimax/h3-max/image-to-video': 'Billed per second by resolution at the post-launch rate: $0.05 at 480P or $0.08 at 768P. Priced automatically from each request\u2019s duration and resolution — leave blank unless you need to override it.',
  'fal-ai/veo3.1/fast/first-last-frame-to-video': 'Billed per second of generated video. Priced automatically from the published rates and each request\u2019s duration, resolution and audio flag — leave blank unless you need to override it.',
  'bytedance/seedance-2.0/image-to-video': 'Billed per second of generated video. Priced automatically from the published 720p and 1080p rates and each request\u2019s duration — leave blank unless you need to override it. 480p has no published rate, so it stays unpriced until one is entered here.',
  'fal-ai/bria/background/remove': 'Billed per generation. Priced automatically from the published rate — leave blank unless you need to override it. Called heavily (the seam search alone fires it ~100 times a job), so it dominates a job\u2019s total.',
  'fal-ai/sam-3/image': 'Billed per request. Priced automatically from the published rate — leave blank unless you need to override it.',
  'Uveye/xfeat-feature-matcher': 'Our own deployed FAL app (the default feature matcher), billed by CPU execution time. Priced automatically from the per-second rate against each attempt\u2019s measured duration, which includes queue wait and so over-states a little — enter a per-call figure to override it.',
  'Uveye/roma-feature-matcher': 'Our own deployed FAL app, billed by GPU execution time. Priced automatically from the published per-second rate against each attempt\u2019s measured duration, which includes queue wait and so over-states a little — enter a per-call figure to override it.',
  'fal-ai/nano-banana-2/edit': 'The last tier of the nano-banana-2 chain (Vertex AI → Google API → fal). The Google tiers report their own token counts and are priced from the published Gemini Flash Image rates, so they need nothing here. fal returns no usage for this endpoint, so enter a per-call figure if you want the calls that do fall through to it priced.',
  'openai/gpt-image-2/edit': 'Billed per image, by output size and quality. Priced automatically from the published per-image table and each request\u2019s own size, quality and reference images — leave blank unless you need to override it.',
  'meta/muse-image/edit': 'Billed at $0.01 per generated image. Priced automatically — leave blank unless you need to override it.',
};

// Known OpenAI endpoints (direct API, tracked under fal_usage with per-call pricing)
const KNOWN_OPENAI_ENDPOINTS = [
  'openai_gpt_image_2',
  'openai_gpt_image_1_5',
];

// Known Azure OpenAI endpoints (tracked under fal_usage with per-call pricing)
const KNOWN_AZURE_ENDPOINTS = [
  'azure_gpt_image_2',
];

// Known Gemini models used in the application
const KNOWN_GEMINI_MODELS = [
  'gemini-3-pro-image-preview',
  'gemini-3.1-flash-image-preview',
  'gemini-2.5-flash',
];

// Suggested pricing from API docs (April 2026), shown as input placeholders
// only — nothing here is persisted until the operator types a figure and saves.
// Per-call prices reflect medium quality at our typical output sizes (~3840x2560).
// Endpoints absent from these maps have no published rate we could verify; they
// are deliberately left blank rather than approximated from a similar model.
const FAL_DEFAULT_PRICES: Record<string, { input: string; output: string; perCall: string }> = {
  'fal-ai/moondream3':          { input: '0.40',  output: '3.50',  perCall: '' },
  'fal-ai/nano-banana-2/edit':  { input: '',      output: '',      perCall: '0.16' },     // $0.16/image at 4K
  'openai/gpt-image-2/edit':    { input: '8.00',  output: '30.00', perCall: '0.11' },     // ~$0.11 at ~4K medium quality
  'fal-ai/gpt-image-1.5/edit':  { input: '8.00',  output: '10.00', perCall: '0.05' },     // ~$0.05 at 1024x1536 medium
  'meta/muse-image/edit':       { input: '',      output: '',      perCall: '0.01' },     // $0.01/image
};
const OPENAI_DEFAULT_PRICES: Record<string, string> = {
  'openai_gpt_image_2':   '0.11',    // ~$0.11 at ~4K medium (same rates as FAL passthrough)
  'openai_gpt_image_1_5': '0.05',    // ~$0.05 at 1024x1536 medium
};
const AZURE_DEFAULT_PRICES: Record<string, string> = {
  'azure_gpt_image_2':    '0.10',    // ~$0.11 at ~4K medium, minus 9% Azure discount (x0.91)
};
const GEMINI_DEFAULT_PRICES: Record<string, { input: string; output: string }> = {
  'gemini-3-pro-image-preview':      { input: '2.00',  output: '12.00' },    // image output billed separately at $120/1M
  'gemini-3.1-flash-image-preview':  { input: '0.50',  output: '3.00' },     // image output billed separately at $60/1M
  'gemini-2.5-flash':                { input: '0.15',  output: '0.60' },
};

/** Marks an endpoint that has no `pricing_settings` row yet, so the greyed
 *  placeholder above it is not mistaken for a stored price. */
function NotSavedBadge() {
  return (
    <span
      className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide bg-warning-100 text-warning-700"
      title="No price stored in the database yet — cost is counted as $0 until one is saved"
    >
      Not saved
    </span>
  );
}

/** `fetchPricingSettings` merges built-in suggestions into the DB rows and
 *  marks them with a negative id; only a positive id is a real
 *  `pricing_settings` row that the backend will price a job against. */
function isStored(setting: PricingSetting): boolean {
  return setting.id > 0;
}

const asPriceString = (value: number | null | undefined): string =>
  value?.toString() || '';

export function MobileSettingsPage() {
  const [_settings, setSettings] = useState<PricingSetting[]>([]);
  // Endpoints/models that actually have a `pricing_settings` row.
  const [savedEndpoints, setSavedEndpoints] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pricing' | 'config'>('pricing');

  // Config YAML state
  const [configYaml, setConfigYaml] = useState<ConfigYamlResponse | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [configCopied, setConfigCopied] = useState(false);
  
  // Form state for FAL endpoints (token-based and/or per-call pricing)
  const [falPrices, setFalPrices] = useState<Record<string, { input: string; output: string; perCall: string }>>({});
  
  // Form state for OpenAI endpoints (per-call pricing)
  const [openaiPrices, setOpenaiPrices] = useState<Record<string, string>>({});

  // Form state for Azure OpenAI endpoints (per-call pricing)
  const [azurePrices, setAzurePrices] = useState<Record<string, string>>({});
  
  // Form state for Gemini models
  const [geminiPrices, setGeminiPrices] = useState<Record<string, { input: string; output: string }>>({});

  // Suggested figures rendered as input placeholders — never persisted.
  const [falHints, setFalHints] = useState<Record<string, { input: string; output: string; perCall: string }>>({});
  const [openaiHints, setOpenaiHints] = useState<Record<string, string>>({});
  const [azureHints, setAzureHints] = useState<Record<string, string>>({});
  const [geminiHints, setGeminiHints] = useState<Record<string, { input: string; output: string }>>({});

  // Load settings on mount
  useEffect(() => {
    log.debug('Entering Settings page — initiating DB connectivity check');
    loadSettings();
  }, []);

  // Lazy-load config_mobile.yaml the first time the user opens that tab
  useEffect(() => {
    if (activeTab === 'config' && configYaml === null && !configLoading) {
      void loadConfigYaml();
    }
  }, [activeTab, configYaml, configLoading]);

  const loadConfigYaml = async () => {
    try {
      setConfigLoading(true);
      setConfigError(null);
      const data = await fetchConfigYaml();
      setConfigYaml(data);
      log.debug(`Config YAML fetch succeeded — found=${data.found}, path=${data.path ?? '(none)'}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load config_mobile.yaml';
      log.error(`Config YAML fetch failed: ${msg}`);
      setConfigError(msg);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleCopyConfigYaml = async () => {
    if (!configYaml?.content) return;
    try {
      await navigator.clipboard.writeText(configYaml.content);
      setConfigCopied(true);
      setTimeout(() => setConfigCopied(false), 2000);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to copy to clipboard');
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPricingSettings();
      log.debug(`DB connection succeeded — fetched ${data.length} pricing setting(s)`);
      setSettings(data);
      
      // Every field starts blank. Only a real `pricing_settings` row fills one
      // in; suggestions (the local *_DEFAULT_PRICES maps and the built-in rows
      // `fetchPricingSettings` merges in) become placeholders instead, so an
      // unsaved figure can't be mistaken for a price the backend will apply.
      const falMap: Record<string, { input: string; output: string; perCall: string }> = {};
      const openaiMap: Record<string, string> = {};
      const azureMap: Record<string, string> = {};
      const geminiMap: Record<string, { input: string; output: string }> = {};
      const falHintMap: Record<string, { input: string; output: string; perCall: string }> = {};
      const openaiHintMap: Record<string, string> = {};
      const azureHintMap: Record<string, string> = {};
      const geminiHintMap: Record<string, { input: string; output: string }> = {};
      
      KNOWN_FAL_ENDPOINTS.forEach(endpoint => {
        falMap[endpoint] = { input: '', output: '', perCall: '' };
        falHintMap[endpoint] = FAL_DEFAULT_PRICES[endpoint] || { input: '', output: '', perCall: '' };
      });
      KNOWN_OPENAI_ENDPOINTS.forEach(endpoint => {
        openaiMap[endpoint] = '';
        openaiHintMap[endpoint] = OPENAI_DEFAULT_PRICES[endpoint] || '';
      });
      KNOWN_AZURE_ENDPOINTS.forEach(endpoint => {
        azureMap[endpoint] = '';
        azureHintMap[endpoint] = AZURE_DEFAULT_PRICES[endpoint] || '';
      });
      KNOWN_GEMINI_MODELS.forEach(model => {
        geminiMap[model] = { input: '', output: '' };
        geminiHintMap[model] = GEMINI_DEFAULT_PRICES[model] || { input: '', output: '' };
      });

      setSavedEndpoints(new Set(data.filter(isStored).map(setting => setting.endpoint_or_model)));
      
      data.forEach(setting => {
        const key = setting.endpoint_or_model;
        const stored = isStored(setting);
        if (setting.provider === 'fal') {
          const entry = {
            input: asPriceString(setting.price_per_input_token),
            output: asPriceString(setting.price_per_output_token),
            perCall: asPriceString(setting.price_per_call),
          };
          falMap[key] = stored ? entry : falMap[key] || { input: '', output: '', perCall: '' };
          if (!stored) falHintMap[key] = entry;
        } else if (setting.provider === 'openai') {
          const entry = asPriceString(setting.price_per_call);
          openaiMap[key] = stored ? entry : openaiMap[key] || '';
          if (!stored) openaiHintMap[key] = entry;
        } else if (setting.provider === 'azure') {
          const entry = asPriceString(setting.price_per_call);
          azureMap[key] = stored ? entry : azureMap[key] || '';
          if (!stored) azureHintMap[key] = entry;
        } else if (setting.provider === 'gemini') {
          const entry = {
            input: asPriceString(setting.price_per_input_token),
            output: asPriceString(setting.price_per_output_token),
          };
          geminiMap[key] = stored ? entry : geminiMap[key] || { input: '', output: '' };
          if (!stored) geminiHintMap[key] = entry;
        }
      });
      
      setFalPrices(falMap);
      setOpenaiPrices(openaiMap);
      setAzurePrices(azureMap);
      setGeminiPrices(geminiMap);
      setFalHints(falHintMap);
      setOpenaiHints(openaiHintMap);
      setAzureHints(azureHintMap);
      setGeminiHints(geminiHintMap);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load settings';
      log.error(`DB connection failed: ${msg}`);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);
      
      const settingsToSave: PricingSettingInput[] = [];
      
      // Collect FAL settings (token-based and/or per-call pricing)
      Object.entries(falPrices).forEach(([endpoint, prices]) => {
        if (prices.input.trim() || prices.output.trim() || prices.perCall.trim()) {
          settingsToSave.push({
            provider: 'fal',
            endpoint_or_model: endpoint,
            price_per_call: prices.perCall.trim() ? parseFloat(prices.perCall) : null,
            price_per_input_token: prices.input.trim() ? parseFloat(prices.input) : null,
            price_per_output_token: prices.output.trim() ? parseFloat(prices.output) : null,
          });
        }
      });
      
      // Collect OpenAI settings (per-call pricing)
      Object.entries(openaiPrices).forEach(([endpoint, price]) => {
        if (price.trim()) {
          settingsToSave.push({
            provider: 'openai',
            endpoint_or_model: endpoint,
            price_per_call: parseFloat(price),
          });
        }
      });
      
      // Collect Azure settings (per-call pricing)
      Object.entries(azurePrices).forEach(([endpoint, price]) => {
        if (price.trim()) {
          settingsToSave.push({
            provider: 'azure',
            endpoint_or_model: endpoint,
            price_per_call: parseFloat(price),
          });
        }
      });
      
      // Collect Gemini settings
      Object.entries(geminiPrices).forEach(([model, prices]) => {
        if (prices.input.trim() || prices.output.trim()) {
          settingsToSave.push({
            provider: 'gemini',
            endpoint_or_model: model,
            price_per_input_token: prices.input.trim() ? parseFloat(prices.input) : null,
            price_per_output_token: prices.output.trim() ? parseFloat(prices.output) : null,
          });
        }
      });
      
      if (settingsToSave.length === 0) {
        setError('No pricing settings to save');
        return;
      }
      
      const saved = await updatePricingSettings(settingsToSave);
      setSettings(saved);
      setSavedEndpoints(prev => new Set([...prev, ...saved.map(s => s.endpoint_or_model)]));
      setSuccessMessage(`Saved ${saved.length} pricing settings`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleFalPriceChange = useCallback((endpoint: string, type: 'input' | 'output' | 'perCall', value: string) => {
    setFalPrices(prev => ({
      ...prev,
      [endpoint]: {
        ...prev[endpoint],
        [type]: value,
      },
    }));
  }, []);
  
  const handleOpenaiPriceChange = useCallback((endpoint: string, value: string) => {
    setOpenaiPrices(prev => ({ ...prev, [endpoint]: value }));
  }, []);

  const handleAzurePriceChange = useCallback((endpoint: string, value: string) => {
    setAzurePrices(prev => ({ ...prev, [endpoint]: value }));
  }, []);

  const handleGeminiPriceChange = useCallback((model: string, type: 'input' | 'output', value: string) => {
    setGeminiPrices(prev => ({
      ...prev,
      [model]: {
        ...prev[model],
        [type]: value,
      },
    }));
  }, []);

  return (
    <div className={`${activeTab === 'config' ? 'max-w-5xl' : 'max-w-4xl'} mx-auto`}>
        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 rounded-lg bg-danger-light border border-danger/30 text-danger-dark text-sm flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
        
        {successMessage && (
          <div className="mb-6 p-4 rounded-lg bg-success-light border border-success/30 text-success-dark text-sm flex items-center gap-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        )}

        {/* Tab Bar */}
        <div className="flex overflow-x-auto border-b border-surface-200 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('pricing')}
            title="API Pricing"
            aria-label="API Pricing"
            className={`shrink-0 px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === 'pricing'
                ? 'text-primary-700'
                : 'text-surface-500 hover:text-surface-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>API Pricing</span>
            </div>
            {activeTab === 'pricing' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 rounded-t" />
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('config')}
            title="Config"
            aria-label="Config"
            className={`shrink-0 px-3 sm:px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === 'config'
                ? 'text-primary-700'
                : 'text-surface-500 hover:text-surface-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Config</span>
            </div>
            {activeTab === 'config' && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 rounded-t" />
            )}
          </button>
        </div>

        {activeTab === 'config' ? (
          <section className="bg-white rounded-xl border border-surface-200 p-6 shadow-card">
            <div className="flex items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-surface-800">Loaded config.yaml</h2>
                  <p className="text-sm text-surface-500">Read-only view of the config file the backend resolved at startup</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadConfigYaml}
                  disabled={configLoading}
                  className="px-3 py-1.5 rounded-md border border-surface-200 text-surface-700 text-sm hover:bg-surface-50 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  title="Reload from server"
                >
                  <svg className={`w-4 h-4 ${configLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reload
                </button>
                <button
                  onClick={handleCopyConfigYaml}
                  disabled={!configYaml?.content}
                  className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                  {configCopied ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Copied
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {configError && (
              <div className="mb-4 p-3 rounded-lg bg-danger-light border border-danger/30 text-danger-dark text-sm">
                {configError}
              </div>
            )}

            {configLoading && configYaml === null ? (
              <div className="flex items-center justify-center py-12">
                <svg className="w-8 h-8 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            ) : configYaml === null ? null : configYaml.found && configYaml.content ? (
              <>
                <div className="mb-3 flex items-center gap-2 text-xs">
                  <span className="text-surface-500">Path:</span>
                  <code className="px-2 py-0.5 rounded bg-surface-100 text-surface-700 font-mono break-all">
                    {configYaml.path}
                  </code>
                </div>
                <pre className="bg-white text-primary-700 text-xs font-mono p-4 rounded-lg border border-surface-200 overflow-auto max-h-[70vh] whitespace-pre">
                  {renderYamlWithComments(configYaml.content)}
                </pre>
              </>
            ) : (
              <div className="p-4 rounded-lg bg-surface-50 border border-surface-200 text-sm text-surface-700 space-y-2">
                <p className="font-medium text-surface-800">No config.yaml found on the server.</p>
                <p>The backend is running with built-in defaults. The server searches the following locations, in order:</p>
                <ul className="list-disc pl-5 text-surface-600">
                  <li><code className="font-mono text-xs">./config.yaml</code> (current working directory)</li>
                  <li><code className="font-mono text-xs">&lt;package root&gt;/config.yaml</code></li>
                  <li><code className="font-mono text-xs">~/.config/360_lightning/config.yaml</code></li>
                </ul>
                {configYaml.path && (
                  <p className="text-xs text-surface-500">Resolved path on disk: <code className="font-mono">{configYaml.path}</code> (unreadable).</p>
                )}
              </div>
            )}
          </section>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <svg className="w-8 h-8 animate-spin text-primary-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : (
          <div className="space-y-8">
            {/* FAL Pricing Section */}
            <section className="bg-white rounded-xl border border-surface-200 p-6 shadow-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-surface-800">FAL API Pricing</h2>
                  <p className="text-sm text-surface-500">Token-based or per-call pricing for each FAL endpoint</p>
                </div>
              </div>

              <p className="mb-4 text-xs text-surface-500">
                Greyed figures are published-rate suggestions, not stored values. Nothing is counted
                against a job until a price is typed in and saved.
              </p>

              <div className="space-y-6">
                {Object.entries(falPrices).map(([endpoint, prices]) => (
                  <div key={endpoint} className="p-4 bg-surface-50 rounded-lg border border-surface-100">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-medium text-surface-800">{endpoint}</h3>
                      {!savedEndpoints.has(endpoint) && <NotSavedBadge />}
                    </div>
                    {FAL_PRICING_NOTES[endpoint] && (
                      <p className="text-xs text-surface-500 mb-3">{FAL_PRICING_NOTES[endpoint]}</p>
                    )}
                    <div className={`grid grid-cols-3 gap-4 ${FAL_PRICING_NOTES[endpoint] ? '' : 'mt-3'}`}>
                      <div>
                        <label className="block text-xs font-medium text-surface-600 mb-1">
                          Input Token Price (per 1M)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={prices.input}
                            onChange={(e) => handleFalPriceChange(endpoint, 'input', e.target.value)}
                            placeholder={falHints[endpoint]?.input || '0.00'}
                            className="w-full pl-7 pr-4 py-2 bg-white border border-surface-200 rounded-lg text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-surface-600 mb-1">
                          Output Token Price (per 1M)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={prices.output}
                            onChange={(e) => handleFalPriceChange(endpoint, 'output', e.target.value)}
                            placeholder={falHints[endpoint]?.output || '0.00'}
                            className="w-full pl-7 pr-4 py-2 bg-white border border-surface-200 rounded-lg text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-surface-600 mb-1">
                          Price per Call ($)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">$</span>
                          <input
                            type="number"
                            step="0.001"
                            min="0"
                            value={prices.perCall}
                            onChange={(e) => handleFalPriceChange(endpoint, 'perCall', e.target.value)}
                            placeholder={falHints[endpoint]?.perCall || '0.000'}
                            className="w-full pl-7 pr-4 py-2 bg-white border border-surface-200 rounded-lg text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* OpenAI Direct API Pricing Section */}
            <section className="bg-white rounded-xl border border-surface-200 p-6 shadow-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-surface-800">OpenAI API Pricing</h2>
                  <p className="text-sm text-surface-500">Set price per image generation call for OpenAI direct API</p>
                </div>
              </div>

              <div className="space-y-6">
                {Object.entries(openaiPrices).map(([endpoint, price]) => (
                  <div key={endpoint} className="p-4 bg-surface-50 rounded-lg border border-surface-100">
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-sm font-medium text-surface-800">
                        {endpoint === 'openai_gpt_image_2' ? 'gpt-image-2' : endpoint === 'openai_gpt_image_1_5' ? 'gpt-image-1.5' : endpoint}
                      </h3>
                      {!savedEndpoints.has(endpoint) && <NotSavedBadge />}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-surface-600 mb-1">
                        Price per Call ($)
                      </label>
                      <div className="relative max-w-xs">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">$</span>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={price}
                          onChange={(e) => handleOpenaiPriceChange(endpoint, e.target.value)}
                          placeholder={openaiHints[endpoint] || '0.000'}
                          className="w-full pl-7 pr-4 py-2 bg-white border border-surface-200 rounded-lg text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Azure OpenAI Pricing Section */}
            <section className="bg-white rounded-xl border border-surface-200 p-6 shadow-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-surface-800">Azure OpenAI Pricing</h2>
                  <p className="text-sm text-surface-500">Set price per image generation call for the Azure OpenAI direct API</p>
                </div>
              </div>

              <div className="space-y-6">
                {Object.entries(azurePrices).map(([endpoint, price]) => (
                  <div key={endpoint} className="p-4 bg-surface-50 rounded-lg border border-surface-100">
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-sm font-medium text-surface-800">
                        {endpoint === 'azure_gpt_image_2' ? 'gpt-image-2' : endpoint}
                      </h3>
                      {!savedEndpoints.has(endpoint) && <NotSavedBadge />}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-surface-600 mb-1">
                        Price per Call ($)
                      </label>
                      <div className="relative max-w-xs">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">$</span>
                        <input
                          type="number"
                          step="0.001"
                          min="0"
                          value={price}
                          onChange={(e) => handleAzurePriceChange(endpoint, e.target.value)}
                          placeholder={azureHints[endpoint] || '0.000'}
                          className="w-full pl-7 pr-4 py-2 bg-white border border-surface-200 rounded-lg text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Gemini Pricing Section */}
            <section className="bg-white rounded-xl border border-surface-200 p-6 shadow-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-surface-800">Gemini API Pricing</h2>
                  <p className="text-sm text-surface-500">Set price per 1M tokens (input/output) for each model</p>
                </div>
              </div>

              <div className="space-y-6">
                {Object.entries(geminiPrices).map(([model, prices]) => (
                  <div key={model} className="p-4 bg-surface-50 rounded-lg border border-surface-100">
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-sm font-medium text-surface-800">{model}</h3>
                      {!savedEndpoints.has(model) && <NotSavedBadge />}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-surface-600 mb-1">
                          Input Token Price (per 1M)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={prices.input}
                            onChange={(e) => handleGeminiPriceChange(model, 'input', e.target.value)}
                            placeholder={geminiHints[model]?.input || '0.00'}
                            className="w-full pl-7 pr-4 py-2 bg-white border border-surface-200 rounded-lg text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-surface-600 mb-1">
                          Output Token Price (per 1M)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 text-sm">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={prices.output}
                            onChange={(e) => handleGeminiPriceChange(model, 'output', e.target.value)}
                            placeholder={geminiHints[model]?.output || '0.00'}
                            className="w-full pl-7 pr-4 py-2 bg-white border border-surface-200 rounded-lg text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary px-6 py-2.5 text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Saving...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        )}
    </div>
  );
}
